import type {Instant, UUID} from './common'
import type {EntityLink} from './entity-link'
import {BadgeVariant} from '@/components/ui/badge'
import {EnumValue} from '@/components/shared/EnumValue'
/* eslint-disable i18next/no-literal-string */

// ========== Enums ==========

export const DelegationStatus = {
  SCHEDULED: 'SCHEDULED',
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  REVOKED: 'REVOKED',
} as const
export type DelegationStatus = (typeof DelegationStatus)[keyof typeof DelegationStatus]

/**
 * Rich enum carrying UI rendering metadata (badge variant) for
 * {@link DelegationStatus} wire values.
 */
export class DelegationStatusBadge extends EnumValue {
    static readonly SCHEDULED = new DelegationStatusBadge(DelegationStatus.SCHEDULED, BadgeVariant.OUTLINE)
    static readonly ACTIVE = new DelegationStatusBadge(DelegationStatus.ACTIVE, BadgeVariant.SUCCESS)
    static readonly EXPIRED = new DelegationStatusBadge(DelegationStatus.EXPIRED, BadgeVariant.WARNING)
    static readonly REVOKED = new DelegationStatusBadge(DelegationStatus.REVOKED, BadgeVariant.DESTRUCTIVE)

    static readonly values: ReadonlyArray<DelegationStatusBadge> = [
        DelegationStatusBadge.SCHEDULED,
        DelegationStatusBadge.ACTIVE,
        DelegationStatusBadge.EXPIRED,
        DelegationStatusBadge.REVOKED,
    ]

    static of(code: DelegationStatus | null | undefined): DelegationStatusBadge | undefined {
        return DelegationStatusBadge.values.find((v) => v.code === code)
    }
}

// ========== Interfaces ==========

export interface Delegation {
  id: UUID
  delegator: EntityLink
  deputy: EntityLink
  workflowStepCode: string
  status: DelegationStatus
  permanent: boolean
  startAt: Instant | null
  endAt: Instant | null
  reason: string | null
  revokedAt: Instant | null
  version: number
  createdAt: Instant
  updatedAt: Instant
}

export interface DelegationCreateRequest {
  deputyIdentityId: UUID
  workflowStepCode: string
  startAt?: Instant | null
  endAt?: Instant | null
  reason?: string | null
}

export interface DelegationUpdateRequest {
  startAt?: Instant | null
  endAt?: Instant | null
  reason?: string | null
  version: number
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const DelegationCreateRequestFields = {
    deputyIdentityId: 'deputyIdentityId',
    workflowStepCode: 'workflowStepCode',
    startAt: 'startAt',
    endAt: 'endAt',
    reason: 'reason',
} as const

export const DelegationResponseFields = {
    id: 'id',
    delegator: 'delegator',
    deputy: 'deputy',
    workflowStepCode: 'workflowStepCode',
    status: 'status',
    permanent: 'permanent',
    startAt: 'startAt',
    endAt: 'endAt',
    reason: 'reason',
    revokedAt: 'revokedAt',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const DelegationUpdateRequestFields = {
    startAt: 'startAt',
    endAt: 'endAt',
    reason: 'reason',
    version: 'version',
} as const
/* eslint-enable i18next/no-literal-string */
